import { Divider } from '@mui/material';

import {
  AutoComplete,
  ButtonGroup,
  Form,
  FormItem,
  Input,
} from '../../../../components';
import { type LatencyPeriod, LatencyPeriodOptions } from './const';
import { useLatencyMetrics } from './hooks/useLatencyMetrics';
import {
  LatencyTimeseriesCard,
  PercentilesByModelCard,
  ModelsSummaryTable,
  FallbacksCard,
  ToolCallsTable,
} from './components';

const LATENCY_FILTER_ITEM_CLASS =
  'w-full !flex-nowrap [&>div:last-child]:min-w-0 [&>div:last-child]:flex-1 [&>div:last-child>div]:w-full [&>div:last-child>div]:min-h-0';
const LATENCY_FILTER_ROW_ITEM_CLASS = `${LATENCY_FILTER_ITEM_CLASS} lg:flex-1 lg:min-w-0`;
const LATENCY_FILTER_LABEL_CLASS =
  'min-h-0 min-w-[8.5rem] shrink-0 whitespace-nowrap mr-1 self-center';

export const LatencyMetricsTab = () => {
  const {
    form,
    setForm,
    agentsOptions,
    metricOptions,
    preset,
    timeseries,
    percentiles,
    models,
    fallbacks,
    toolCalls,
  } = useLatencyMetrics();

  return (
    <>
      <div className="mx-2 sm:mx-4 lg:mx-sm">
        <div className="w-full max-w-screen-2xl mx-auto px-2 sm:px-4 pt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 items-start">
            <Form
              key="latency-filters"
              initialValue={{
                agentId: form.agentId,
                conversationId: form.conversationId,
              }}
              onChange={values =>
                setForm({
                  ...form,
                  agentId: values.agentId ?? '',
                  conversationId: values.conversationId ?? '',
                })
              }
            >
              <div className="flex w-full flex-col gap-md lg:flex-row lg:items-center">
                <FormItem
                  inline
                  className={LATENCY_FILTER_ROW_ITEM_CLASS}
                  name="agentId"
                  label="Agent:"
                  labelClassName="text-small text-secondary"
                  labelContainerClassName={LATENCY_FILTER_LABEL_CLASS}
                >
                  <AutoComplete className="w-full" options={agentsOptions} />
                </FormItem>
                <FormItem
                  inline
                  className={LATENCY_FILTER_ROW_ITEM_CLASS}
                  name="conversationId"
                  label="Conversation:"
                  labelClassName="text-small text-secondary"
                  labelContainerClassName={LATENCY_FILTER_LABEL_CLASS}
                >
                  <Input className="w-full" placeholder="conversation id" />
                </FormItem>
              </div>
            </Form>

            <div className="w-full flex items-center lg:justify-end">
              <ButtonGroup
                className="w-full lg:w-auto"
                buttons={LatencyPeriodOptions.map(option => ({
                  action: option.value,
                  label: option.label,
                  active: form.period === option.value,
                }))}
                orientation="horizontal"
                buttonClassName="p-4"
                labelClassName="whitespace-nowrap overflow-hidden text-ellipsis text-small font-semibold text-secondary"
                onClick={action =>
                  setForm({ ...form, period: action as LatencyPeriod })
                }
              />
            </div>
          </div>
        </div>
      </div>

      <Divider className="mx-2 sm:mx-4 lg:mx-sm my-4" />

      <div className="mx-2 sm:mx-4 lg:mx-sm pb-8">
        <div className="w-full max-w-screen-2xl mx-auto px-2 sm:px-4 space-y-4 sm:space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
            <ModelsSummaryTable data={models.data} loading={models.loading} />
            <ToolCallsTable data={toolCalls.data} loading={toolCalls.loading} />
          </div>

          <FallbacksCard data={fallbacks.data} loading={fallbacks.loading} />

          <Divider />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
            <Form
              key="latency-metric-filter"
              initialValue={{ metricKey: form.metricKey }}
              onChange={values =>
                setForm({
                  ...form,
                  metricKey: values.metricKey || form.metricKey,
                })
              }
            >
              <FormItem
                inline
                className={LATENCY_FILTER_ITEM_CLASS}
                name="metricKey"
                label="Metric:"
                labelClassName="text-small text-secondary"
                labelContainerClassName={LATENCY_FILTER_LABEL_CLASS}
              >
                <AutoComplete className="w-full" options={metricOptions} />
              </FormItem>
            </Form>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 items-stretch">
            <LatencyTimeseriesCard
              data={timeseries.data}
              loading={timeseries.loading}
              period={form.period}
              metricLabel={preset.label}
              unit={preset.unit}
            />
            <PercentilesByModelCard
              data={percentiles.data}
              loading={percentiles.loading}
              metricLabel={preset.label}
              unit={preset.unit}
            />
          </div>
        </div>
      </div>
    </>
  );
};
